import {
  CONDITIONAL_SECTION_NODE,
  CONDITIONAL_SPAN_NODE,
  isSectionLike,
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
} from '@seen/core/observation-logic/documentUnits'
import { Button } from '@seen/ui'
import { IconSettings, IconX } from '@tabler/icons-react'
import { type Editor, useEditorState } from '@tiptap/react'
import { type JSX, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { ConditionSheet } from '#src/components/rich-text-editor/conditional-display/ConditionSheet.tsx'
import { NODE_CONFIG_KEY } from '#src/components/rich-text-editor/conditional-display/NodeConfigExtension.ts'
import { useNodeConfigPanelSlot } from '#src/components/rich-text-editor/conditional-display/NodeConfigPanelSlot.tsx'

function titleFor(name: string | undefined): string {
  switch (name) {
    case CONDITIONAL_SECTION_NODE:
      return 'Conditional section'
    case CONDITIONAL_SPAN_NODE:
      return 'Conditional text'
    case OBSERVATION_PANEL_NODE:
      return 'Configure instrument'
    default:
      return 'Configure field'
  }
}

function PanelHeader({
  title,
  onClose,
}: {
  title: string
  onClose: () => void
}): JSX.Element {
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
      <span className="flex min-w-0 items-center gap-2">
        <IconSettings className="size-4 shrink-0" />
        <h2 className="truncate text-sm font-medium">{title}</h2>
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Close panel"
        className="size-7"
        onClick={onClose}
      >
        <IconX className="size-4" />
      </Button>
    </div>
  )
}

// The configuration panel for the node `openNodeConfig` names. On a template
// page it sits beside the content, which narrows to make room; elsewhere it
// is a non-modal sheet, so the note stays usable while it is open.
export function NodeConfigSheetHost({
  editor,
}: {
  editor: Editor | null
}): JSX.Element | null {
  const slot = useNodeConfigPanelSlot()
  useEffect(() => {
    const storage = editor?.storage.nodeConfig
    if (!storage) {
      return
    }
    storage.hosts += 1
    return () => {
      storage.hosts -= 1
    }
  }, [editor])
  const target = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      const pos = current
        ? (NODE_CONFIG_KEY.getState(current.state)?.pos ?? null)
        : null
      const node =
        current && pos !== null ? current.state.doc.nodeAt(pos) : null
      if (
        !node ||
        pos === null ||
        (node.type.name !== OBSERVATION_NODE && !isSectionLike(node.type.name))
      ) {
        return null
      }
      return { pos, node }
    },
  })
  if (!editor) {
    return null
  }
  const close = (): void => {
    editor.commands.closeNodeConfig()
  }
  const form = target && (
    <ConditionSheet
      key={`${target.node.attrs.id ?? target.pos}`}
      editor={editor}
      pos={target.pos}
      node={target.node}
      onDone={close}
    />
  )
  const title = titleFor(target?.node.type.name)

  if (slot) {
    return target
      ? createPortal(
          <aside
            aria-label={title}
            className="flex h-full w-[400px] min-w-0 flex-col border-l border-border bg-background"
            onKeyDownCapture={(event) => {
              // A popover opened from the panel closes on its own Escape.
              if (
                event.key === 'Escape' &&
                event.currentTarget.contains(event.target as Node)
              ) {
                close()
              }
            }}
            // Configuring a node does not disengage it.
            data-keeps-node-engaged=""
          >
            <PanelHeader title={title} onClose={close} />
            {form}
          </aside>,
          slot
        )
      : null
  }
  return (
    <Sheet
      open={!!target}
      onOpenChange={(open) => !open && close()}
      modal={false}
    >
      <SheetContent
        side="right"
        className="flex w-[400px] max-w-full flex-col gap-0 p-0"
        disableScrollArea
        hideClose
        onInteractOutside={(event) => event.preventDefault()}
        // Configuring a node does not disengage it.
        data-keeps-node-engaged=""
      >
        <PanelHeader title={title} onClose={close} />
        {form}
      </SheetContent>
    </Sheet>
  )
}
